/**
 * Display numbering for a proposal's action cards.
 *
 * The number beside an action ("2. Update deal: …") is a POSITION IN THE LIST
 * THE USER IS LOOKING AT — nothing else. It is therefore derived from what
 * actually renders, never from `execution_order`.
 *
 * `execution_order` is assigned at proposal creation and is invisible to the
 * user. Three things routinely make it disagree with the rendered list, and
 * each one shows up as a gap the user has to explain to themselves:
 *   1. A fully no-op action is hidden (live no-op detection), so its order is
 *      spent on a card nobody sees — the list starts at "2.".
 *   2. `mergeActions` folds a create plus its associations into ONE card,
 *      while every absorbed association still consumed an order.
 *   3. Groups render in sequence, so numbering per group would restart at 1
 *      partway down the list — the same confusion wearing a different hat.
 *
 * So numbering walks the groups in render order, spends one number per
 * rendered card/row, and is proposal-wide. Absorbed associations map to their
 * card's number: they render inside it, so that is where they are.
 */

import type { TypedActionGroup } from './actionGrouping';
import { mergeActions } from './actionMerging';

export interface ActionDisplayNumbering {
  /** actionId → 1-based position of the card/row it renders in. */
  numbers: ReadonlyMap<number, number>;
  /** Total cards/rows the proposal renders — the "N" in "1..N". */
  total: number;
}

const EMPTY_NUMBERING: ActionDisplayNumbering = {
  numbers: new Map(),
  total: 0,
};

/**
 * Assign 1..N across every card/row the proposal renders, in render order.
 *
 * Table and mixed groups render one row per action; accordion groups render
 * one card per merged item. Both are walked in the order the group renders
 * them, so the numbers read top-to-bottom with no gaps.
 */
export function buildActionDisplayNumbering(
  groups: TypedActionGroup[]
): ActionDisplayNumbering {
  if (groups.length === 0) {
    return EMPTY_NUMBERING;
  }

  const numbers = new Map<number, number>();
  let next = 1;

  for (const group of groups) {
    if (group.viewType === 'accordion') {
      for (const item of mergeActions(group.actions)) {
        const position = next++;
        if (item.type === 'merged') {
          // Every constituent maps to the card's position: the absorbed
          // associations render inside it, not as rows of their own.
          for (const id of item.merged.allActionIds) {
            numbers.set(id, position);
          }
        } else {
          numbers.set(item.action.id, position);
        }
      }
      continue;
    }

    // 'table' and 'mixed' render one row per action, in group order.
    for (const action of group.actions) {
      numbers.set(action.id, next++);
    }
  }

  return { numbers, total: next - 1 };
}
